import type { LocalHorizontal } from "./coordinates.ts";
import type { HorizontalBounds, PolygonWithHoles } from "./geometry.ts";
import type {
  RuntimeLinearFeature,
  RuntimeWorldTilePayloadV2,
} from "./schema/runtimeTile.ts";
import type { WorldTileId } from "./schema/tile.ts";

export type MinimapLineKind = "road" | "path" | "railway" | "water";
export type MinimapAreaKind = "vegetation" | "building";

export interface MinimapLineFeature {
  readonly id: string;
  readonly sourceId: string;
  readonly kind: MinimapLineKind;
  readonly points: readonly LocalHorizontal[];
  /** Source-supplied physical width. Null means the map uses a symbolic stroke. */
  readonly widthM: number | null;
}

export interface MinimapAreaFeature {
  readonly id: string;
  readonly sourceId: string;
  readonly kind: MinimapAreaKind;
  readonly polygons: readonly PolygonWithHoles[];
}

export interface MinimapTileFeatures {
  readonly tileId: WorldTileId;
  readonly bounds: HorizontalBounds;
  readonly lines: readonly MinimapLineFeature[];
  readonly areas: readonly MinimapAreaFeature[];
}

function compareCodeUnits(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function translatedPoint(
  point: Readonly<LocalHorizontal>,
  origin: Readonly<LocalHorizontal>,
): LocalHorizontal {
  return {
    x: origin.x + point.x,
    z: origin.z + point.z,
  };
}

function translatedPolygon(
  polygon: Readonly<PolygonWithHoles>,
  origin: Readonly<LocalHorizontal>,
): PolygonWithHoles {
  return {
    outer: polygon.outer.map((point) => translatedPoint(point, origin)),
    holes: polygon.holes.map((hole) =>
      hole.map((point) => translatedPoint(point, origin))
    ),
  };
}

function squaredDistanceToSegment(
  point: Readonly<LocalHorizontal>,
  from: Readonly<LocalHorizontal>,
  to: Readonly<LocalHorizontal>,
): number {
  const segmentX = to.x - from.x;
  const segmentZ = to.z - from.z;
  const lengthSquared = segmentX * segmentX + segmentZ * segmentZ;
  if (lengthSquared <= Number.EPSILON) {
    const deltaX = point.x - from.x;
    const deltaZ = point.z - from.z;
    return deltaX * deltaX + deltaZ * deltaZ;
  }
  const projection = Math.max(
    0,
    Math.min(
      1,
      (
        (point.x - from.x) * segmentX +
        (point.z - from.z) * segmentZ
      ) / lengthSquared,
    ),
  );
  const nearestX = from.x + segmentX * projection;
  const nearestZ = from.z + segmentZ * projection;
  const deltaX = point.x - nearestX;
  const deltaZ = point.z - nearestZ;
  return deltaX * deltaX + deltaZ * deltaZ;
}

/**
 * Deterministic Ramer-Douglas-Peucker reduction for presentation-only lines.
 * Source endpoints are always preserved; polygon rings are never simplified.
 */
export function simplifyMinimapLine(
  points: readonly Readonly<LocalHorizontal>[],
  toleranceM: number,
): readonly LocalHorizontal[] {
  if (!Number.isFinite(toleranceM) || toleranceM < 0) {
    throw new Error("Minimap line tolerance must be finite and non-negative");
  }
  if (points.length <= 2 || toleranceM === 0) {
    return points.map((point) => ({ ...point }));
  }
  const kept = new Uint8Array(points.length);
  kept[0] = 1;
  kept[points.length - 1] = 1;
  const toleranceSquared = toleranceM * toleranceM;
  const ranges: Array<readonly [number, number]> = [[0, points.length - 1]];
  while (ranges.length > 0) {
    const range = ranges.pop();
    if (range === undefined) {
      break;
    }
    const [start, end] = range;
    const from = points[start];
    const to = points[end];
    if (from === undefined || to === undefined || end - start <= 1) {
      continue;
    }
    let furthestIndex = -1;
    let furthestDistanceSquared = toleranceSquared;
    for (let index = start + 1; index < end; index += 1) {
      const point = points[index];
      if (point === undefined) {
        continue;
      }
      const distanceSquared = squaredDistanceToSegment(point, from, to);
      if (distanceSquared > furthestDistanceSquared) {
        furthestDistanceSquared = distanceSquared;
        furthestIndex = index;
      }
    }
    if (furthestIndex >= 0) {
      kept[furthestIndex] = 1;
      ranges.push([start, furthestIndex], [furthestIndex, end]);
    }
  }
  return points.flatMap((point, index) => kept[index] === 1 ? [{ ...point }] : []);
}

function sourceWidth(feature: Readonly<RuntimeLinearFeature>): number | null {
  for (const value of [
    feature.carriagewayWidthM,
    feature.totalWidthM,
    feature.widthM,
  ]) {
    if (value !== undefined && Number.isFinite(value) && value > 0) {
      return value;
    }
  }
  return null;
}

function extractedLine(
  feature: Readonly<RuntimeLinearFeature>,
  origin: Readonly<LocalHorizontal>,
  kind: MinimapLineKind,
  toleranceM: number,
): MinimapLineFeature | null {
  if (feature.points.length < 2) {
    return null;
  }
  const points = simplifyMinimapLine(
    feature.points.map((point) => translatedPoint(point, origin)),
    toleranceM,
  );
  return points.length < 2
    ? null
    : {
        id: feature.id,
        sourceId: feature.sourceId,
        kind,
        points,
        widthM: sourceWidth(feature),
      };
}

function isVegetatedArea(classCode: string): boolean {
  return classCode === "ms0" ||
    classCode === "sd15" ||
    classCode.endsWith(":vegetated") ||
    classCode.endsWith(":wood");
}

/**
 * Builds a compact, serializable UI view of an already-validated runtime tile.
 * No new geography, labels, or marker semantics are introduced here.
 */
export function extractMinimapTileFeatures(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  lineSimplificationToleranceM: number,
): MinimapTileFeatures {
  const lines = [
    ...tile.transportLines.flatMap((feature) => {
      if (
        feature.kind !== "road" &&
        feature.kind !== "path" &&
        feature.kind !== "railway"
      ) {
        return [];
      }
      const line = extractedLine(
        feature,
        tile.origin,
        feature.kind,
        lineSimplificationToleranceM,
      );
      return line === null ? [] : [line];
    }),
    ...tile.waterLines.flatMap((feature) => {
      const line = extractedLine(
        feature,
        tile.origin,
        "water",
        lineSimplificationToleranceM,
      );
      return line === null ? [] : [line];
    }),
  ].sort((left, right) => compareCodeUnits(left.id, right.id));

  const vegetation = tile.landCoverMasks
    .filter((feature) => isVegetatedArea(feature.classCode))
    .map((feature): MinimapAreaFeature => ({
      id: feature.id,
      sourceId: feature.sourceId,
      kind: "vegetation",
      polygons: feature.polygons.map((polygon) =>
        translatedPolygon(polygon, tile.origin)
      ),
    }));
  const buildings = tile.buildings.map((building): MinimapAreaFeature => ({
    id: building.id,
    sourceId: building.sourceId,
    kind: "building",
    polygons: [translatedPolygon(building.footprint, tile.origin)],
  }));

  return {
    tileId: tile.id,
    bounds: { ...tile.bounds },
    lines,
    areas: [...vegetation, ...buildings].sort((left, right) =>
      compareCodeUnits(left.id, right.id)
    ),
  };
}

export function minimapTileIntersectsCircle(
  bounds: Readonly<HorizontalBounds>,
  center: Readonly<LocalHorizontal>,
  radiusM: number,
): boolean {
  if (!Number.isFinite(radiusM) || radiusM < 0) {
    throw new Error("Minimap radius must be finite and non-negative");
  }
  const nearestX = Math.max(bounds.minX, Math.min(bounds.maxX, center.x));
  const nearestZ = Math.max(bounds.minZ, Math.min(bounds.maxZ, center.z));
  return Math.hypot(center.x - nearestX, center.z - nearestZ) <= radiusM;
}

export function projectMinimapPoint(
  point: Readonly<LocalHorizontal>,
  center: Readonly<LocalHorizontal>,
  radiusM: number,
  sizePx: number,
): Readonly<{ x: number; y: number }> {
  if (!Number.isFinite(radiusM) || radiusM <= 0) {
    throw new Error("Minimap projection radius must be finite and positive");
  }
  if (!Number.isFinite(sizePx) || sizePx <= 0) {
    throw new Error("Minimap projection size must be finite and positive");
  }
  const pixelsPerMetre = sizePx / (radiusM * 2);
  return {
    x: sizePx / 2 + (point.x - center.x) * pixelsPerMetre,
    // Runtime +Z is south, so increasing Z correctly moves down a north-up map.
    y: sizePx / 2 + (point.z - center.z) * pixelsPerMetre,
  };
}

export function minimapHeadingVector(
  yawRadians: number,
): Readonly<{ x: number; y: number }> {
  if (!Number.isFinite(yawRadians)) {
    throw new Error("Minimap yaw must be finite");
  }
  return {
    x: Math.sin(yawRadians),
    y: Math.cos(yawRadians),
  };
}

export function minimapCardinalDirection(yawRadians: number): string {
  const directionNames = [
    "north",
    "north-east",
    "east",
    "south-east",
    "south",
    "south-west",
    "west",
    "north-west",
  ] as const;
  const bearingFromNorth = Math.atan2(
    Math.sin(yawRadians),
    -Math.cos(yawRadians),
  );
  const index = Math.round(
    ((bearingFromNorth + Math.PI * 2) % (Math.PI * 2)) / (Math.PI / 4),
  ) % directionNames.length;
  return directionNames[index]!;
}
